import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';

import { useSoundStatus } from '../../context/SoundStatusContext';

import Modal from '../UI/modal';
import Hint from '../UI/hint';
import FallingBlock from '../UI/game/falling-block';
import Column from '../UI/game/column';
import FooterMessage from '../UI/game/footer-message';
import Header from '../UI/game/header';

// import TutorialProcessSound from './sounds/tutorial-process.mp3';
import ClickSound from './sounds/click.mp3';
import ItemHitSpikeSound from './sounds/item-hit-spike.mp3';
import AnswerCorrectSound from './sounds/answer-correct.mp3';
import AnswerWrongSound from './sounds/answer-wrong.mp3';

import './styles.css';

import { 
  tutorialElementsBlockItems,
  tutorialElementsOptions
} from './data';

const maxPlayTime = 18000;

const blockHeight = 32;
const blockMaxWidth = 92;
const fallingAreaBottomGap = 19;
const blockFallingSpeed = 5;

export default function ClassificationTutorialElements() {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = location.search;

  const params = useParams();
  const levelNumber = Number(params.levelNumber);

  const { soundStatus } = useSoundStatus();

  const clickEffect = useRef(new Audio(ClickSound));
  const itemHitSpikeEffect = useRef(new Audio(ItemHitSpikeSound));
  const answerCorrectEffect = useRef(new Audio(AnswerCorrectSound));
  const answerWrongEffect = useRef(new Audio(AnswerWrongSound));

  useEffect(() => {
    const playClickSound = () => {
      if (soundStatus === 'enabled') {
        clickEffect.current.play().catch(e => console.error('Error playing click sound:', e));
      }
    };

    document.addEventListener('click', playClickSound);

    return () => {
      document.removeEventListener('click', playClickSound);
    };
  }, [soundStatus]);

  const fallingAreaRef = useRef(null);

  const [isFooterMessageShown, setIsFooterMessageShown] = useState(false);
  const [footerMessageType, setFooterMessageType] = useState();
  const [footerMessageDescription, setFooterMessageDescription] = useState();

  const handleMouseEnter = (id, hoverContent) => {
    setHoveredItem(id);

    setHintContent(hoverContent);
    setIsHintShown(true);
  }

  const handleMouseLeave = () => {
    setHoveredItem(0);
    setIsHintShown(false);
  }

  const handleMouseDown = () => {
    setHoveredItem(0);
    setIsHintShown(false);
  }

  const showFooterMessage = (type, description) => {
    setFooterMessageType(type);
    setFooterMessageDescription(description);
    setIsFooterMessageShown(true);
  }

  const [isHintShown, setIsHintShown] = useState(true);
  const [isHintOverlayShown, setIsHintOverlayShown] = useState(true);
  const [isHintButtonShown, setIsHintButtonShown] = useState(true);

  const [hintContent, setHintContent] = useState(<span>In this game, you are going to place <b>the brick (i.e. the financial statement item)</b> to the correct <b>financial statement</b>.</span>);

  const handleStartGame = (e) => {
    e.stopPropagation();
    setIsPlaying(true);
    setHintContent(<span>Where should <b>Assets</b> be placed? Let’s try to <b>drag and drop</b> it under <b>{tutorialElementsOptions[0].name}</b>.</span>);
    setIsHintButtonShown(false);
    setIsHintOverlayShown(false);
  };

  const handleReadyButtonClick = () => {
    navigate(`/classification/level/${levelNumber}${searchParams}`);
  }

  const [isModalShown, setIsModalShown] = useState(false);
  const modalContent = 
    <>
      <div className="modal-content">
        <p>Awesome! You’ve nailed this game already.</p>
        <p>You can get a higher score by <b>clearing the bricks accurately and fast</b>.</p>
        <p>Let’s start the real game.</p>
      </div>
      <div className="next-button" onClick={handleReadyButtonClick}>I’m Ready!</div>
    </>;

  const [isPlaying, setIsPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const timeRef = useRef(time);
  useEffect(() => {
    timeRef.current = time;
  }, [time]);

  useEffect(() => {
    if (!isPlaying) return;

    const timerInterval = setInterval(() => {
      const newTime = timeRef.current + 1;
      setTime(newTime);
      if (newTime >= maxPlayTime) {
        clearInterval(timerInterval);
      }
    }, 1000);

    return () => clearInterval(timerInterval);
  }, [isPlaying]);

  const [fallingAreaWidth, setFallingAreaWidth] = useState(0);
  const [fallingAreaHeight, setFallingAreaHeight] = useState(0);

  useEffect(() => {
    const updateFallingAreaSizes = () => {
      const width = fallingAreaRef.current?.clientWidth || 0;
      const height = fallingAreaRef.current?.clientHeight || 0;
      setFallingAreaWidth(width);
      setFallingAreaHeight(height);
    };

    updateFallingAreaSizes();
    window.addEventListener('resize', updateFallingAreaSizes);

    return () => window.removeEventListener('resize', updateFallingAreaSizes);
  }, []);

  const [fallingBlocks, setFallingBlocks] = useState([]);
  const [availableBlockItems, setAvailableBlockItems] = useState([...tutorialElementsBlockItems]);
  const [hoveredItem, setHoveredItem] = useState(0);
  const [initialBlockAdded, setInitialBlockAdded] = useState(false);

  const availableBlockItemsRef = useRef(availableBlockItems);

  useEffect(() => {
    availableBlockItemsRef.current = availableBlockItems;
  }, [availableBlockItems]);

  // Show the first block immediately (before game starts)
  useEffect(() => {
    if (fallingAreaWidth > 0 && !initialBlockAdded) {
      const firstBlock = tutorialElementsBlockItems[0];
      const isLeftSide = Math.random() < 0.5;
      let left = 'auto';
      let right = 'auto';

      if (isLeftSide) {
        left = Math.random() * (fallingAreaWidth / 2 - 25) + 5;
      } else {
        right = Math.random() * (fallingAreaWidth / 2 - 25) + 5;
      }

      const newBlock = {
        id: Date.now(),
        name: firstBlock.name,
        answers: firstBlock.answers,
        description: firstBlock.description,
        startTime: firstBlock.startTime || Date.now(),
        accuracyCoefficient: firstBlock.accuracyCoefficient,
        left: left,
        right: right,
        y: 0,
        width: blockMaxWidth,
      };

      setFallingBlocks([newBlock]);
      setAvailableBlockItems([]);
      setInitialBlockAdded(true);
    }
  }, [fallingAreaWidth, initialBlockAdded]);

  useEffect(() => {
    if (!isPlaying) return;
  
    const generateRandomBlock = () => {
      if (availableBlockItemsRef.current.length === 0) {
        return;
      }
      const randomIndex = Math.floor(Math.random() * availableBlockItemsRef.current.length);
      const randomBlock = availableBlockItemsRef.current[randomIndex];
      
      const isLeftSide = Math.random() < 0.5;
      let left = 'auto';
      let right = 'auto';
  
      if (isLeftSide) {
        left = Math.random() * (fallingAreaWidth / 2 - 25) + 5;
      } else {
        right = Math.random() * (fallingAreaWidth / 2 - 25) + 5;
      }
  
      const newBlock = {
        id: Date.now(),
        name: randomBlock.name,
        answers: randomBlock.answers,
        description: randomBlock.description,
        startTime: randomBlock.startTime || Date.now(),
        accuracyCoefficient: randomBlock.accuracyCoefficient,
        left: left,
        right: right,
        y: 0,
        width: blockMaxWidth,
      };
  
      setFallingBlocks(prevBlocks => [...prevBlocks, newBlock]);
  
      setAvailableBlockItems(prev => prev.filter((_, index) => index !== randomIndex));
    };
  
    // Initial call
    generateRandomBlock();
  
    // Interval calls
    const generateRandomBlockInterval = setInterval(generateRandomBlock, 3000);
    return () => clearInterval(generateRandomBlockInterval);
  }, [isPlaying, fallingAreaWidth]);
  
  const fallBlocks = () => {
    const bottomGap = blockHeight + fallingAreaBottomGap + blockFallingSpeed;
    setFallingBlocks((prevBlocks) => {
      const updatedBlocks = prevBlocks.map(block => {
        const blockY = hoveredItem === block.id ? block.y : block.y + blockFallingSpeed;
        return({
          ...block,
          y: blockY,
        })
      });

      const blocksToRemove = updatedBlocks.filter(block => block.y > fallingAreaHeight - bottomGap);

      if (blocksToRemove.length > 0) {
        if (soundStatus === 'enabled') {
          itemHitSpikeEffect.current.play().catch(e => console.log('Error playing item hit spike sound:', e));
        }
        
        const newAvailableBlocks = blocksToRemove.map(block => ({
          name: block.name,
          answers: block.answers,
          description: block.description,
          startTime: block.startTime,
          accuracyCoefficient: block.accuracyCoefficient
        }));

        setAvailableBlockItems(prevItems => {
          const filteredNewBlocks = newAvailableBlocks.filter(newBlock => 
            !prevItems.some(item => item.name === newBlock.name)
          );

          return [...prevItems, ...filteredNewBlocks];
        });

        return updatedBlocks.filter(block => !blocksToRemove.includes(block));
      }

      return updatedBlocks;
    });

  };

  useEffect(() => {
    if (!isPlaying) return;
    const fallBlocksInterval = setInterval(fallBlocks, 1000);
  
    return () => {
      clearInterval(fallBlocksInterval);
    };
  }, [isPlaying, fallingAreaHeight, hoveredItem]);
    
  const handleDrop = (item, column, callback) => {
    const isCorrect = item.answers.includes(column.name);

    const result = { is_correct: isCorrect };

    callback(null, result);

    const footerMessagePrefix = '';

    const footerMessageMiddle = result.is_correct
      ? ' belongs in '
      : ' does NOT belong in ';

    const footerMessageSuffix = '.';

    const footerMessage = <p>{footerMessagePrefix}<b>{item.name}</b>{footerMessageMiddle}<b>{column.name}</b>{footerMessageSuffix}</p>;

    if(isCorrect) {
      if (soundStatus === 'enabled') {
        answerCorrectEffect.current.play().catch(e => console.log('Error playing answer correct sound:', e));
      }
      showFooterMessage('success', footerMessage);
      setTimeout(() => {
        setIsModalShown(true);
        setIsPlaying(false);
      }, 1000);
    } else {
      if (soundStatus === 'enabled') {
        answerWrongEffect.current.play().catch(e => console.log('Error playing answer wrong sound:', e));
      }
      showFooterMessage('error', footerMessage);
      const { name, answers, description, startTime } = item;
      setAvailableBlockItems(prevAvailableBlockItems => [
        ...prevAvailableBlockItems,
        { name, answers, description, startTime, accuracyCoefficient: 0.5 }
      ]);
    }

    setFallingBlocks(prevBlocks => prevBlocks.filter(block => block.id !== item.id));
  };

  return (
    <div className="game-level-wrapper">
      {
        isHintOverlayShown &&
          <div className="background-overlay"/>
      }
      <Header 
        time={time}
        totalScore={0}
      />
      <section className="main-section">
        <div className="container columns">
          <Column
            side='left'
            options={tutorialElementsOptions[0]}
            levelNumber={1}
            handleDrop={handleDrop}
            gameType="Account Classification"
          />
          <main className="content">
            <div className="falling-area-container">
              <div className="falling-area" ref={fallingAreaRef}>
                {fallingBlocks.map((block) => (
                  <FallingBlock
                    key={block.id}
                    id={block.id}
                    name={block.name}
                    answers={block.answers}
                    description={block.description}
                    startTime={block.startTime}
                    accuracyCoefficient={block.accuracyCoefficient}
                    left={block.left}
                    right={block.right}
                    y={block.y}  
                    onMouseEnter={isPlaying ? () => handleMouseEnter(block.id, block.description) : undefined}
                    onMouseLeave={isPlaying ? handleMouseLeave : undefined}
                    onMouseDown={isPlaying ? handleMouseDown : undefined}
                    isDraggable={isPlaying}
                    {...(isHintShown && {
                      additionalContent: (
                        <Hint
                          content={hintContent}
                          buttonText={isHintButtonShown ? "Next": null}
                          onButtonClick={isHintButtonShown ? handleStartGame : undefined}
                        />
                      )
                    })}
                  />
                ))}
              </div>
            </div>
          </main>
          <Column
            side='right'
            options={tutorialElementsOptions[1]}
            levelNumber={1}
            handleDrop={handleDrop}
            gameType="Account Classification"
          />
        </div>
      </section>
      <footer className="game-footer">
        {isFooterMessageShown && (
          <FooterMessage
            type={footerMessageType}
            description={footerMessageDescription}
          />
        )}
      </footer>
      {isModalShown && (
        <Modal
          content={modalContent}
        />
      )}
    </div>
  );
}
